import {
  AST,
  Constant,
  Erod,
  Expression,
  MakeArray,
  MakeStruct,
  RelationRef,
} from '@cotera/era';
import { DG } from '@cotera/sdk/core';
import { Assert, partitionRecords, partitionResults } from '@cotera/utilities';
import _ from 'lodash';
import { err, ok, Result } from 'neverthrow';
import {
  isUtcISO,
  NoSchemaDayMonthYearMsg,
  NoSchemaRangeMsg,
  NoSchemaRecordMsg,
  NoSchemaSuperMsg,
} from './erod-schema-form';

/**
 * Finds the discriminated union option whose discriminator literal matches the given value.
 * Returns the first matching option, or the first option if no match (for default/empty forms).
 */
const optionForDiscriminatorValue = (
  discriminator: string,
  value: string | null,
  options: readonly Erod.Def<unknown>[]
): Erod.Def<unknown> => {
  const firstOption = Assert.assertExists(options[0]);
  if (value === null || value === '') {
    return firstOption;
  }
  const matching = options.find((opt) => {
    if (opt.ty.ty.k !== 'struct') {
      return false;
    }
    const fieldTy = opt.ty.ty.fields[discriminator];
    if (fieldTy === undefined) {
      return false;
    }
    if (fieldTy.ty.k !== 'enum') {
      return false;
    }
    return fieldTy.ty.t.includes(value);
  });
  return matching ?? firstOption;
};

/**
 * Extracts the discriminator field value from form values when they represent a struct.
 */
const discriminatorValueFromFieldTree = (
  fieldTree: FieldTree,
  discriminator: string
): string | null => {
  if (!('struct' in fieldTree)) {
    return null;
  }
  const discriminatorFormValues = fieldTree.struct[discriminator];
  if (discriminatorFormValues === undefined) {
    return null;
  }
  if (discriminatorFormValues.t !== 'fieldTree') {
    return null;
  }
  const sub = discriminatorFormValues.fieldTree;
  if (!('baseValue' in sub)) {
    return null;
  }
  return sub.baseValue;
};

/**
 * Extracts the discriminator field value from a make-struct AST.
 */
const discriminatorValueFromAst = (
  ast: AST.ExprFR,
  discriminator: string
): string | null => {
  if (ast.t !== 'make-struct') {
    return null;
  }
  const fieldAst = ast.fields[discriminator];
  if (fieldAst === undefined) {
    return null;
  }
  if (fieldAst.t !== 'scalar') {
    return null;
  }
  const val = fieldAst.val;
  return typeof val === 'string' ? val : null;
};

/**
 * The form values of a ErodSchemaForm.
 *
 * Represents a tree whose structure is analogous to
 * the shape of a schema and whose base value is a
 * string representation or eraQL representation.
 *
 * NOTE: The empty string always represents null. See specs for
 * stringOfPrimitive() and primitiveOfString()
 *
 * Note: if the string is representing time, it is always an ISO string in UTC timezone
 * (meaning ending with letter Z with no timezone offset).
 */
export type FormValues = {
  t: 'fieldTree' | 'eraQL' | 'colRef';
  fieldTree: FieldTree;
  eraQL: string;
  colRef: string;
};

/**
 * A tree of fields, whose leaves are string input fields or
 * subTrees represented as eraQL or colRef.
 */
export type FieldTree =
  | {
      struct: Record<string, FormValues>;
    }
  | {
      array: FormValues[];
    }
  | {
      baseValue: string;
    };

/**
 * Given a schema, returns default values for a corresponding schema form.
 */
export const defaultFormValuesOfSchema = (
  schema: Erod.Def<any>
): FormValues => {
  // At the moment, ignores schema default values

  return {
    t: 'fieldTree',
    fieldTree: defaultFieldTreeOfSchema(schema),
    eraQL: '',
    colRef: '',
  };
};

const defaultFieldTreeOfSchema = (schema: Erod.Def<any>): FieldTree => {
  Assert.assert(
    schema.shape.t !== 'record' && schema.shape.t !== 'recordWithKey',
    NoSchemaRecordMsg
  );

  switch (schema.shape.t) {
    case 'discriminatedUnion': {
      const firstOption = Assert.assertExists(schema.shape.options[0]);
      return defaultFieldTreeOfSchema(firstOption);
    }

    case 'lazy': {
      const innerSchema = schema.shape.get();
      return defaultFieldTreeOfSchema(innerSchema);
    }

    case 'struct': {
      return {
        struct: _.mapValues(schema.shape.fieldsSchema, (fieldSchema) =>
          defaultFormValuesOfSchema(fieldSchema)
        ),
      };
    }

    case 'array': {
      return {
        array: [],
      };
    }

    case 'base': {
      return {
        baseValue: '',
      };
    }

    default:
      return Assert.unreachable(schema.shape);
  }
};

/**
 * Given the values of a schema form, produce the underlying Expression.
 */
export const exprOfFormValues = (
  formValues: FormValues,
  schema: Erod.Def<any>,
  relativeParser: DG.Graph.RelativeParser,
  relationRef: RelationRef
): Result<Expression, any> => {
  if (schema.shape.t === 'discriminatedUnion') {
    const { discriminator, options } = schema.shape;
    const discriminatorValue =
      formValues.t === 'fieldTree' && 'struct' in formValues.fieldTree
        ? discriminatorValueFromFieldTree(formValues.fieldTree, discriminator)
        : null;
    const option = optionForDiscriminatorValue(
      discriminator,
      discriminatorValue,
      options
    );
    return exprOfFormValues(formValues, option, relativeParser, relationRef);
  }
  if (schema.shape.t === 'lazy') {
    const innerSchema = schema.shape.get();
    return exprOfFormValues(
      formValues,
      innerSchema,
      relativeParser,
      relationRef
    );
  }

  switch (formValues.t) {
    case 'fieldTree': {
      const { fieldTree } = formValues;
      if ('struct' in fieldTree) {
        Assert.assert(schema.shape.t === 'struct');

        const fieldsExprRes = _.mapValues(
          schema.shape.fieldsSchema,
          (fieldSchema, fieldName) =>
            exprOfFormValues(
              Assert.assertExists(fieldTree.struct[fieldName]),
              fieldSchema,
              relativeParser,
              relationRef
            )
        );

        const [okFields, errFields] = partitionRecords(fieldsExprRes);

        if (Object.keys(errFields).length > 0) {
          return err({ t: 'field-errors', fields: errFields });
        }

        return ok(MakeStruct(okFields));
      } else if ('array' in fieldTree) {
        Assert.assert(schema.shape.t === 'array');

        const { elementSchema } = schema.shape;

        const elementsExprRes = fieldTree.array.map((eltFormValues) =>
          exprOfFormValues(
            eltFormValues,
            elementSchema,
            relativeParser,
            relationRef
          )
        );

        const [okElts, errElts] = partitionResults(elementsExprRes);

        if (errElts.length > 0) {
          return err({ t: 'array-errors', elements: errElts });
        }

        // Array Expressions are not allowed to contain null elements
        const nonNullElts = okElts.filter((elt) => !isAstConstantNull(elt.ast));

        if (nonNullElts.length === 0) {
          // Array Expressions are not allowed to have 0 elements (would cause typecheck error)
          return ok(Constant(null, schema.ty));
        }

        return ok(MakeArray(nonNullElts));
      } else if ('baseValue' in fieldTree) {
        return exprOfBaseFormValue(fieldTree.baseValue, schema);
      }

      return Assert.unreachable(fieldTree);
    }

    case 'eraQL': {
      if (formValues.eraQL === '') {
        return ok(Constant(null, schema.ty));
      }

      return relativeParser.parseExpr(formValues.eraQL);
    }

    case 'colRef': {
      if (formValues.colRef === '') {
        return ok(Constant(null, schema.ty));
      }

      return ok(relationRef.attr(formValues.colRef));
    }

    default:
      return Assert.unreachable(formValues.t);
  }
};

export const exprOfBaseFormValue = (
  baseValue: string,
  baseSchema: Erod.Def<any>
): Result<Expression, any> => {
  // By precondition
  Assert.assert(baseSchema.shape.t === 'base');
  // By Erod.Schema.shape spec
  Assert.assert(
    baseSchema.ty.ty.k === 'primitive' ||
      baseSchema.ty.ty.k === 'enum' ||
      baseSchema.ty.ty.k === 'range'
  );
  Assert.assert(baseSchema.ty.ty.k !== 'range', NoSchemaRangeMsg);

  if (baseValue === '') {
    return ok(Constant(null, baseSchema.ty));
  }

  switch (baseSchema.ty.ty.k) {
    case 'primitive': {
      Assert.assert(baseSchema.ty.ty.t !== 'super', NoSchemaSuperMsg);
      Assert.assert(
        baseSchema.ty.ty.t !== 'day' &&
          baseSchema.ty.ty.t !== 'month' &&
          baseSchema.ty.ty.t !== 'year',
        NoSchemaDayMonthYearMsg
      );

      switch (baseSchema.ty.ty.t) {
        case 'string': {
          return ok(Constant(baseValue, baseSchema.ty));
        }

        case 'boolean': {
          Assert.assert(baseValue === 'true' || baseValue === 'false');
          const b = baseValue === 'true';
          return ok(Constant(b, baseSchema.ty));
        }

        case 'int':
        case 'float': {
          const num = Number(baseValue);
          if (Number.isNaN(num)) {
            return err(
              `non-numerical value in int/float form field: ${baseValue}`
            );
          }
          return ok(Constant(num, baseSchema.ty));
        }

        case 'timestamp': {
          if (!isUtcISO(baseValue)) {
            return err(
              'form value for a timestamp must be empty string or ISO'
            );
          }

          return ok(Constant(new Date(baseValue)));
        }

        default:
          return Assert.unreachable(baseSchema.ty.ty.t);
      }
    }

    case 'enum': {
      if (baseSchema.ty.ty.t.includes(baseValue)) {
        return ok(Constant(baseValue, baseSchema.ty));
      } else {
        return err({ t: 'scalar-does-not-match-valid-enum-option' });
      }
    }

    default:
      return Assert.unreachable(baseSchema.ty.ty);
  }
};

export const formValuesOfExpr = (
  expr: Expression,
  schema: Erod.Def<any>
): FormValues => formValuesOfAst(expr.ast, schema);

const formValuesOfAst = (
  ast: AST.ExprFR,
  schema: Erod.Def<any>
): FormValues => {
  if (ast.t === 'attr') {
    return {
      t: 'colRef',
      fieldTree: defaultFieldTreeOfSchema(schema),
      eraQL: '',
      colRef: ast.name,
    };
  }

  Assert.assert(
    schema.shape.t !== 'record' && schema.shape.t !== 'recordWithKey',
    NoSchemaRecordMsg
  );

  switch (schema.shape.t) {
    case 'discriminatedUnion': {
      if (isAstConstantNull(ast)) {
        return defaultFormValuesOfSchema(schema);
      }
      const { discriminator, options } = schema.shape;
      const discriminatorValue =
        ast.t === 'make-struct'
          ? discriminatorValueFromAst(ast, discriminator)
          : null;
      const option = optionForDiscriminatorValue(
        discriminator,
        discriminatorValue,
        options
      );
      return formValuesOfAst(ast, option);
    }

    case 'lazy': {
      const innerSchema = schema.shape.get();
      return formValuesOfAst(ast, innerSchema);
    }

    case 'struct': {
      Assert.assert(ast.t === 'make-struct'); // At the moment, assuming structs are only make-structs (not scalars)

      return {
        t: 'fieldTree',
        fieldTree: {
          struct: _.mapValues(
            schema.shape.fieldsSchema,
            (fieldSchema, fieldName) =>
              formValuesOfAst(
                Assert.assertExists(ast.fields[fieldName]),
                fieldSchema
              )
          ),
        },
        eraQL: '',
        colRef: '',
      };
    }

    case 'array': {
      // null represents the empty array.

      if (isAstConstantNull(ast)) {
        return {
          t: 'fieldTree',
          fieldTree: { array: [] },
          eraQL: '',
          colRef: '',
        };
      }

      Assert.assert(ast.t === 'make-array');

      const { elementSchema } = schema.shape;

      return {
        t: 'fieldTree',
        fieldTree: {
          array: ast.elements.map((eltAst) =>
            formValuesOfAst(eltAst, elementSchema)
          ),
        },
        eraQL: '',
        colRef: '',
      };
    }

    case 'base':
      return baseFormValueOfAst(ast, schema);

    default:
      return Assert.unreachable(schema.shape);
  }
};

/**
 * Returns a BaseFormValue given an Expression's AST that fulfills a base schema.
 *
 * Requires schema.shape.t === 'base'
 */
const baseFormValueOfAst = (
  ast: AST.ExprFR,
  schema: Erod.Def<any>
): FormValues => {
  Assert.assert(schema.shape.t === 'base'); // by precondition
  Assert.assert(
    // by schema.shape specs
    schema.ty.ty.k === 'primitive' ||
      schema.ty.ty.k === 'enum' ||
      schema.ty.ty.k === 'range'
  );
  Assert.assert(
    schema.ty.ty.k !== 'range',
    'Impossible to create range schemas (at the moment).'
  );

  if (isAstConstantNull(ast)) {
    // Null is always represented as empty string field (if not empty array)
    return {
      t: 'fieldTree',
      fieldTree: { baseValue: '' },
      eraQL: '',
      colRef: '',
    };
  }

  if (schema.ty.ty.t === 'timestamp') {
    const timestampRes = isConstantDateTime(ast);

    Assert.assert(timestampRes.t === true);

    return {
      t: 'fieldTree',
      fieldTree: { baseValue: timestampRes.utcIso },
      eraQL: '',
      colRef: '',
    };
  }

  // At this point and onwards in execution, all non-scalers are to be represented as EraQL Expressions.

  if (ast.t !== 'scalar') {
    // TODO validation of schema
    const eraQL = Expression.fromAst(ast).toEraQL();
    return {
      t: 'eraQL',
      fieldTree: defaultFieldTreeOfSchema(schema),
      eraQL,
      colRef: '',
    };
  }

  // At this point and onwards in execution, we are dealing with scalars to be represented as string input fields.

  Assert.assert(schema.ty.ty.t !== 'super', NoSchemaSuperMsg);
  Assert.assert(
    schema.ty.ty.t !== 'day' &&
      schema.ty.ty.t !== 'month' &&
      schema.ty.ty.t !== 'year',
    NoSchemaDayMonthYearMsg
  );

  const stringInputValue: string = (() => {
    switch (schema.ty.ty.k) {
      case 'primitive': {
        switch (schema.ty.ty.t) {
          case 'string':
            Assert.assert(typeof ast.val === 'string');
            return ast.val;

          case 'boolean':
            Assert.assert(typeof ast.val === 'boolean');
            return ast.val ? 'true' : 'false';

          case 'int':
          case 'float': {
            Assert.assert(typeof ast.val === 'number');
            return ast.val.toString();
          }

          default:
            return Assert.unreachable(schema.ty.ty.t);
        }
      }

      case 'enum': {
        Assert.assert(typeof ast.val === 'string');
        return ast.val;
      }

      default:
        return Assert.unreachable(schema.ty.ty);
    }
  })();

  return {
    t: 'fieldTree',
    fieldTree: { baseValue: stringInputValue },
    eraQL: '',
    colRef: '',
  };
};

const isAstConstantNull = (ast: AST.ExprFR): boolean => {
  const exprRes = Expression.tryfromAst(ast);

  if (exprRes.isErr()) {
    return false;
  }

  const isNullRes = exprRes.value.isNull().tryEvaluate();

  if (isNullRes.isErr()) {
    return false;
  }

  return Boolean(isNullRes.value);
};

/**
 * Returns whether the AST represents a constant datetime.
 *
 * Implementation note:
 *  - Ideally the logic in the `byEvaluation` block will be sufficient,
 * however Expr.tryEvaluate() fails on casts of strings to timestamps.
 *  - So we also first check if the AST is a cast of string to timestamp.
 */
const isConstantDateTime = (
  ast: AST.ExprFR
): { t: true; utcIso: string } | { t: false } => {
  const byCast: { t: true; utcIso: string } | { t: false } = (() => {
    if (
      ast.t === 'cast' &&
      ast.targetTy.k === 'primitive' &&
      ast.targetTy.t === 'timestamp'
    ) {
      const childRes = Expression.fromAst(ast.expr).tryEvaluate();

      if (childRes.isErr()) {
        return { t: false };
      }

      if (typeof childRes.value === 'string' && isUtcISO(childRes.value)) {
        return { t: true, utcIso: childRes.value };
      } else {
        return { t: false };
      }
    }

    return { t: false };
  })();

  if (byCast.t === true) {
    return byCast;
  }

  const byEvaluation: { t: true; utcIso: string } | { t: false } = (() => {
    const exprRes = Expression.tryfromAst(ast);

    if (exprRes.isErr()) {
      return { t: false };
    }

    const dateTimeRes = exprRes.value.tryEvaluate();

    if (dateTimeRes.isErr()) {
      return { t: false };
    }

    if (dateTimeRes.value instanceof Date) {
      const isoString = dateTimeRes.value.toISOString();

      Assert.assert(isUtcISO(isoString));

      return { t: true, utcIso: isoString };
    } else {
      return { t: false };
    }
  })();

  return byEvaluation;
};
